import React, { useEffect, useRef, useState } from 'react';
import { Loader2, MapPin } from 'lucide-react';

interface GooglePlacesMapViewerProps {
  onPlaceSelect: (place: google.maps.places.PlaceResult) => void;
  onPlaceConfirm?: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  height?: string;
  showSearchInput?: boolean;
  selectedPlace?: google.maps.places.PlaceResult | null;
  onClearSelection?: () => void;
}

// Global loading state to prevent multiple simultaneous loads
let googleMapsLoading: Promise<void> | null = null;
let googleMapsLoaded = false;

const GooglePlacesMapViewer: React.FC<GooglePlacesMapViewerProps> = ({
  onPlaceSelect,
  onPlaceConfirm,
  placeholder = "Search for your business...",
  className = "",
  disabled = false,
  height = "400px",
  showSearchInput = false,
  selectedPlace = null,
  onClearSelection
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [internalSelectedPlace, setInternalSelectedPlace] = useState<google.maps.places.PlaceResult | null>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const infoWindowRef = useRef<any>(null);
  const autocompleteRef = useRef<any>(null);
  const isMountedRef = useRef(true);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    isInitializedRef.current = false;

    const loadGoogleMaps = async (): Promise<void> => {
      // If already loaded, return immediately
      if (googleMapsLoaded && window.google?.maps?.importLibrary) {
        return;
      }

      // If currently loading, wait for that to complete
      if (googleMapsLoading) {
        return googleMapsLoading;
      }

      // Start a new load
      googleMapsLoading = (async () => {
        try {
          const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

          if (!apiKey) {
            throw new Error('VITE_GOOGLE_MAPS_API_KEY environment variable is not set');
          }

          // Check if bootstrap loader already exists
          if (!window.google?.maps?.importLibrary) {
            // Load the Google Maps bootstrap loader
            const script = document.createElement('script');
            script.innerHTML = `
              (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=\`https://maps.\${c}apis.com/maps/api/js?\`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
                key: "${apiKey}",
                v: "weekly",
                language: "en",
                region: "US"
              });
            `;

            document.head.appendChild(script);

            // Wait for the loader to be available
            let attempts = 0;
            while (!window.google?.maps?.importLibrary && attempts < 50) {
              await new Promise(resolve => setTimeout(resolve, 100));
              attempts++;
            }

            if (!window.google?.maps?.importLibrary) {
              throw new Error('Google Maps loader failed to initialize after 5 seconds');
            }
          }

          // Load required libraries
          await window.google.maps.importLibrary("maps");
          await window.google.maps.importLibrary("places");

          googleMapsLoaded = true;

        } catch (err) {
          googleMapsLoading = null; // Reset on error so it can be retried
          throw err;
        } finally {
          googleMapsLoading = null;
        }
      })();

      return googleMapsLoading;
    };

    const loadAndInitialize = async () => {
      try {
        await loadGoogleMaps();

        // Check if component is still mounted before proceeding
        if (!isMountedRef.current) {
          return;
        }

        await initializeMapAndAutocomplete();

        if (isMountedRef.current) {
          setIsLoading(false);
        }

      } catch (err) {
        console.error('GooglePlacesMapViewer: Error loading Google Maps:', err);
        if (isMountedRef.current) {
          setError(`Error loading Google Maps: ${err}`);
          setIsLoading(false);
        }
      }
    };

    const initializeMapAndAutocomplete = async () => {
      // Prevent duplicate initialization
      if (isInitializedRef.current) {
        return;
      }

      // Wait for mapContainerRef to be available (may not be ready immediately on remount)
      let attempts = 0;
      while (!mapContainerRef.current && isMountedRef.current && attempts < 30) {
        await new Promise(resolve => setTimeout(resolve, 50));
        attempts++;
      }

      if (!mapContainerRef.current) {
        console.error('GooglePlacesMapViewer: mapContainerRef not available after waiting');
        return;
      }

      if (!window.google?.maps) {
        console.error('GooglePlacesMapViewer: Google Maps API not available');
        return;
      }

      if (!isMountedRef.current) {
        return;
      }

      try {
        isInitializedRef.current = true;

        // Bratislava center coordinates
        const center = { lat: 48.1486, lng: 17.1077 };
        
        // Initialize the map
        const map = new window.google.maps.Map(mapContainerRef.current, {
          center,
          zoom: 13,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        
        mapRef.current = map;
        
        // Create the PlaceAutocompleteElement
        const placeAutocomplete = new window.google.maps.places.PlaceAutocompleteElement({
          locationBias: center,
          includedPrimaryTypes: ['establishment'],
        });
        
        // Set placeholder if supported
        if (placeAutocomplete.placeholder !== undefined) {
          placeAutocomplete.placeholder = placeholder;
        }
        
        // Style the autocomplete element to match Google Search
        
        autocompleteRef.current = placeAutocomplete;
        
        if (showSearchInput && searchInputRef.current) {
          // Add to external search input container
          searchInputRef.current.appendChild(placeAutocomplete);
        } else {
          // Add as map control (original behavior)
          placeAutocomplete.style.margin = '10px';
          placeAutocomplete.style.width = '300px';
          
          const card = document.createElement('div');
          card.style.backgroundColor = 'white';
          card.style.border = '2px solid #fff';
          card.style.borderRadius = '8px';
          card.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
          card.style.margin = '10px';
          card.style.padding = '5px';
          card.appendChild(placeAutocomplete);
          
          // Add the card to the map as a control
          map.controls[window.google.maps.ControlPosition.TOP_LEFT].push(card);
        }
        
        const marker = new window.google.maps.Marker({
          map,
        });
        markerRef.current = marker;
        
        const infoWindow = new window.google.maps.InfoWindow({});
        infoWindowRef.current = infoWindow;

        // Add event listener for place selection from autocomplete
        placeAutocomplete.addEventListener('gmp-select', async (event: any) => {
          try {
  
            const { placePrediction } = event;
            
            // Convert to Place object
            const place = placePrediction.toPlace();
            
            // Fetch the fields we need
            await place.fetchFields({ 
              fields: [
                'displayName', 
                'formattedAddress', 
                'location',
                'id', // place_id
                'internationalPhoneNumber',
                'websiteURI',
                'regularOpeningHours',
                'types'
              ] 
            });


            // Update map view
            if (place.viewport) {
              map.fitBounds(place.viewport);
            } else if (place.location) {
              map.setCenter(place.location);
              map.setZoom(17);
            }
            
            if (place.location) {
              marker.setPosition(place.location);
            }
            
            // Create info window content
            const content = `
              <div style="padding: 8px;">
                <div style="font-weight: bold; font-size: 16px; margin-bottom: 4px; color: #4285f4;">
                  ${place.displayName || 'Unknown'}
                </div>
                <div style="color: #666; font-size: 14px;">
                  ${place.formattedAddress || 'No address'}
                </div>
              </div>
            `;
            
            // Show info window
            infoWindow.setContent(content);
            if (place.location) {
              infoWindow.setPosition(place.location);
            }
            infoWindow.open({
              map,
              anchor: marker,
              shouldFocus: false,
            });

            // Log the raw place data
            console.log('=== GOOGLE PLACES RAW DATA ===');
            console.log('Place object:', place);
            console.log('regularOpeningHours:', place.regularOpeningHours);
            
            // Convert to our expected format and set as selected
            const placeResult: google.maps.places.PlaceResult = {
              place_id: place.id,
              name: place.displayName,
              formatted_address: place.formattedAddress,
              formatted_phone_number: place.internationalPhoneNumber,
              website: place.websiteURI,
              types: place.types,
              geometry: place.location ? {
                location: place.location
              } : undefined,
              opening_hours: (() => {
                try {
                  const regularHours = place.regularOpeningHours;
                  
                  console.log('=== PROCESSING OPENING HOURS ===');
                  console.log('regularHours:', regularHours);
                  
                  if (regularHours) {
                    const periods = regularHours.periods;
                    const weekdayText = regularHours.weekdayDescriptions;
                    
                    console.log('periods:', periods);
                    console.log('weekdayDescriptions:', weekdayText);
                    
                    const result = {
                      periods: periods?.map((period: any) => ({
                        open: period.open ? {
                          day: period.open.day,
                          time: period.open.time
                        } : undefined,
                        close: period.close ? {
                          day: period.close.day,
                          time: period.close.time
                        } : undefined
                      })) || [],
                      weekday_text: weekdayText || []
                    };
                    
                    console.log('Formatted opening_hours result:', result);
                    return result;
                  }
                  console.log('No regularOpeningHours found');
                  return undefined;
                } catch (error) {
                  console.error('Error processing opening hours:', error);
                  return undefined;
                }
              })()
            };

            console.log('=== FINAL PLACE RESULT ===');
            console.log('placeResult:', placeResult);
            console.log('placeResult.opening_hours:', placeResult.opening_hours);
            
            setInternalSelectedPlace(placeResult);
            // Call onPlaceSelect immediately when place is selected from autocomplete
            onPlaceSelect(placeResult);
            
          } catch (error) {
            console.error('GooglePlacesMapViewer: Error in gmp-select handler:', error);
          }
        });

        // Add comprehensive CSS for Google Search styling and dark mode support
        const addGoogleSearchStyles = () => {
          // Remove existing styles first
          const existingStyle = document.getElementById('google-places-custom-styles');
          if (existingStyle) {
            existingStyle.remove();
          }
          
          const style = document.createElement('style');
          style.id = 'google-places-custom-styles';
          style.textContent = `
            /* Hide unwanted Google Maps elements */
            .gm-style .gm-style-iw-c {
              display: none !important;
            }
            .gm-style .gm-style-iw-tc {
              display: none !important;
            }
            
            /* Google Places Autocomplete Element Styling */
            gmp-place-autocomplete {
              /* Base styles from SelectTrigger/Input */
              display: flex;
              width: 100%;
              align-items: center;
              justify-content: space-between;
              height: 2rem; /* h-8 */
              border-radius: calc(var(--radius) - 2px); /* rounded-md */
              border: 1px solid hsl(var(--input)); /* border border-input */
              padding: 0.5rem 0.75rem; /* py-2 px-3 */
              font-size: 0.875rem; /* text-sm */
              background-color: hsl(var(--background)); /* bg-background */
              color: hsl(var(--foreground)); /* text-foreground */
              outline: none; /* focus:outline-none */
              transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
            }

            /* Placeholder styling */
            gmp-place-autocomplete::placeholder {
              color: hsl(var(--muted-foreground)); /* placeholder:text-muted-foreground */
            }

            /* Focus styles */
            gmp-place-autocomplete:focus-within {
              outline: 2px solid transparent; /* focus:outline-none */
              outline-offset: 2px; /* focus:outline-none */
              box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow, 0 0 #0000); /* focus:ring-2 */
              --tw-ring-offset-width: 2px; /* focus:ring-offset-2 */
              --tw-ring-offset-color: hsl(var(--background)); /* ring-offset-background */
              --tw-ring-color: hsl(var(--ring)); /* focus:ring-ring */
            }

            /* Dark mode specific focus ring color to ensure no blue */
            .dark gmp-place-autocomplete:focus-within {
              --tw-ring-color: transparent !important; /* Explicitly make ring transparent in dark mode */
            }

            /* Dark mode specific styles */
            .dark gmp-place-autocomplete {
              background-color: rgb(55 65 81 / var(--tw-bg-opacity, 1)); /* dark:bg-gray-700 */
              color: rgb(243 244 246 / var(--tw-text-opacity, 1)); /* dark:text-gray-100 */
              border-color: rgb(75 85 99 / var(--tw-border-opacity, 1)); /* dark:border-gray-600 */
            }

            .dark gmp-place-autocomplete::placeholder {
              color: hsl(var(--muted-foreground)) !important; /* Ensure dark mode placeholder is correct */
            }

            /* Ensure internal input inherits styles and removes its own focus */
            gmp-place-autocomplete input {
              background-color: inherit !important;
              color: inherit !important;
              border: none !important;
              outline: none !important;
              box-shadow: none !important;
              padding: 0 !important;
              font-size: inherit !important;
              height: 100% !important;
              width: 100% !important;
            }

            gmp-place-autocomplete input:focus,
            gmp-place-autocomplete input:active {
              outline: none !important;
              box-shadow: none !important;
              border: none !important;
            }

            /* Hide the default focus ring from Google's component */
            gmp-place-autocomplete .focus-ring,
            gmp-place-autocomplete div[class*="focus-ring"],
            gmp-place-autocomplete div[style*="border: 2px solid"],
            gmp-place-autocomplete div[style*="rgb(26, 115, 232)"] {
              display: none !important;
              opacity: 0 !important;
              visibility: hidden !important;
              border: none !important;
              box-shadow: none !important;
              width: 0 !important;
              height: 0 !important;
            }

            /* Ensure no blue borders from Google's component */
            gmp-place-autocomplete * {
              border-color: transparent !important;
            }

            /* Dropdown suggestions styling - Light mode */
            .pac-container {
              background-color: hsl(var(--popover)); /* bg-popover */
              border-radius: calc(var(--radius) - 2px); /* rounded-md */
              box-shadow: var(--tw-shadow); /* shadow-md */
              --tw-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
              border: 1px solid hsl(var(--border)); /* border */
              margin-top: 4px;
              font-family: inherit; /* Inherit font from body */
              overflow: hidden;
              z-index: 9999 !important; /* Ensure it's above other elements */
              position: absolute !important;
            }
            
            /* Dropdown suggestions styling - Dark mode */
            .dark .pac-container {
              background-color: hsl(var(--popover)) !important; /* dark:bg-popover */
              border-color: hsl(var(--border)) !important; /* dark:border-border */
              box-shadow: var(--tw-shadow) !important;
            }
            
            /* Individual suggestion items - Light mode */
            .pac-item {
              background-color: hsl(var(--popover)); /* bg-popover */
              color: hsl(var(--popover-foreground)); /* text-popover-foreground */
              padding: 0.375rem 0.75rem; /* py-1.5 pl-8 pr-2 (adjusted for icon) */
              border-bottom: 1px solid hsl(var(--border)); /* border-b */
              font-size: 0.875rem; /* text-sm */
              line-height: 1.25rem; /* text-sm */
              cursor: pointer;
              transition: background-color 0.15s ease;
              display: flex; /* To align icon and text */
              align-items: center;
            }
            
            /* Individual suggestion items - Dark mode */
            .dark .pac-item {
              background-color: hsl(var(--popover)) !important; /* dark:bg-popover */
              color: hsl(var(--popover-foreground)) !important; /* dark:text-popover-foreground */
              border-bottom-color: hsl(var(--border)) !important; /* dark:border-border */
            }
            
            /* Hover state - Light mode */
            .pac-item:hover,
            .pac-item-selected {
              background-color: hsl(var(--accent)); /* focus:bg-accent */
              color: hsl(var(--accent-foreground)); /* focus:text-accent-foreground */
            }
            
            /* Hover state - Dark mode */
            .dark .pac-item:hover,
            .dark .pac-item-selected {
              background-color: hsl(var(--accent)) !important; /* dark:focus:bg-accent */
              color: hsl(var(--accent-foreground)) !important; /* dark:focus:text-accent-foreground */
            }
            
            /* Remove last border */
            .pac-item:last-child {
              border-bottom: none !important;
            }
            
            /* Query text styling - Light mode */
            .pac-item-query {
              color: hsl(var(--foreground)); /* text-foreground */
              font-weight: 500; /* font-medium */
            }
            
            /* Query text styling - Dark mode */
            .dark .pac-item-query {
              color: hsl(var(--foreground)) !important; /* dark:text-foreground */
            }
            
            /* Icon styling - Light mode */
            .pac-icon {
              background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%235f6368"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>');
              background-size: 1rem 1rem; /* size-4 */
              width: 1rem; /* size-4 */
              height: 1rem; /* size-4 */
              margin-right: 0.5rem; /* mr-2 */
              flex-shrink: 0; /* shrink-0 */
            }
            
            /* Icon styling - Dark mode */
            .dark .pac-icon {
              background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%239aa0a6"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>') !important;
            }
            
            /* Hide the default pac-icon-marker */
            .pac-icon-marker {
              display: none !important;
            }
            
            /* Suggestion text styling */
            .pac-item span {
              color: hsl(var(--muted-foreground)); /* text-muted-foreground */
              font-size: 0.875rem; /* text-sm */
            }
            
            .dark .pac-item span {
              color: hsl(var(--muted-foreground)) !important; /* dark:text-muted-foreground */
            }
            
            /* Main text of suggestion */
            .pac-item-query span {
              color: hsl(var(--foreground)); /* text-foreground */
              font-weight: 500; /* font-medium */
            }
            
            .dark .pac-item-query span {
              color: hsl(var(--foreground)) !important; /* dark:text-foreground */
            }
            
            /* Powered by Google footer */
            .pac-logo {
              padding: 0.5rem 0.75rem; /* py-2 px-3 */
              background-color: hsl(var(--background)); /* bg-background */
              border-top: 1px solid hsl(var(--border)); /* border-t */
            }
            
            .dark .pac-logo {
              background-color: hsl(var(--card)) !important; /* dark:bg-card */
              border-top-color: hsl(var(--border)) !important; /* dark:border-border */
            }
          `;
          document.head.appendChild(style);
        };
        
        // Apply the CSS immediately
        addGoogleSearchStyles();
        
        // Re-apply styles when theme changes (observe for theme changes)
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && 
                (mutation.attributeName === 'class' || mutation.attributeName === 'data-theme')) {
              setTimeout(addGoogleSearchStyles, 10); // Small delay to ensure DOM is updated
            }
          });
        });
        
        // Watch for theme changes on document element
        observer.observe(document.documentElement, {
          attributes: true,
          attributeFilter: ['data-theme', 'class']
        });
        
        // Cleanup observer on component unmount
        return () => observer.disconnect();

      } catch (err) {
        console.error('GooglePlacesMapViewer: Error initializing map and autocomplete:', err);
        setError(`Error initializing map: ${err}`);
      }
    };

    if (!disabled) {
      loadAndInitialize();
    }

    // Cleanup
    return () => {
      isMountedRef.current = false;
      isInitializedRef.current = false;

      try {
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
          infoWindowRef.current = null;
        }
        if (markerRef.current) {
          markerRef.current.setMap(null);
          markerRef.current = null;
        }
        if (mapRef.current) {
          mapRef.current = null;
        }
        if (autocompleteRef.current) {
          autocompleteRef.current = null;
        }
        // Remove custom styles on unmount
        const existingStyle = document.getElementById('google-places-custom-styles');
        if (existingStyle) {
          existingStyle.remove();
        }
      } catch (error) {
        console.error('GooglePlacesMapViewer: Error during cleanup:', error);
      }
    };
  }, [placeholder, disabled, height]);


  if (disabled) {
    return (
      <div className={`flex items-center justify-center p-8 border rounded-md bg-gray-50 ${className}`} style={{ height }}>
        <span className="text-gray-500">Map viewer disabled</span>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search Input (when showSearchInput is true) */}
      {showSearchInput && (
        <div className="space-y-2">
          <div
            ref={searchInputRef}
            className="w-full"
          />
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="flex items-center justify-center space-x-2 p-8 border rounded-md bg-gray-50" style={{ height }}>
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          <span className="text-gray-500">Loading Google Maps...</span>
        </div>
      )}

      {/* Error Overlay */}
      {error && (
        <div className="flex items-center justify-center space-x-2 p-8 border rounded-md bg-red-50 border-red-200" style={{ height }}>
          <MapPin className="h-6 w-6 text-red-400" />
          <span className="text-red-600">{error}</span>
        </div>
      )}

      {/* Map Container - Always rendered but hidden when loading/error */}
      <div
        ref={mapContainerRef}
        className="google-places-map-viewer border rounded-md"
        style={{
          height,
          width: '100%',
          display: (isLoading || error) ? 'none' : 'block'
        }}
      />

    </div>
  );
};

// Add Google Maps to window type
declare global {
  interface Window {
    google?: any;
  }
}

export default GooglePlacesMapViewer;